import { createApi } from '@reduxjs/toolkit/query/react'
import { customBaseQuery } from 'Shared/helpers'

const memberIdParams = ({ memberId }) => ({ member_id: memberId })

// POST to one of the roster urls; params come from the mutation argument.
const post = (build, urlKey, toParams = () => ({}), invalidatesTags = undefined) => build.mutation({
  query: (arg) => ({ urlKey, method: 'POST', params: toParams(arg) }),
  invalidatesTags,
})

export const rosterApi = createApi({
  reducerPath: 'rosterApi',
  baseQuery:   customBaseQuery,
  tagTypes:    ['Roster', 'MasterRoster'],
  endpoints:   (build) => ({
    getRoster: build.query({
      query: ({ filterLetters, column, sortOrder, page }) => ({
        urlKey: 'fetchRosterUrl',
        params: {
          filter_letters: filterLetters,
          column,
          sort_order: sortOrder,
          page,
        },
      }),
      providesTags: ['Roster'],
    }),

    searchMasterRoster: build.query({
      query: ({ search, page }) => ({
        urlKey: 'masterRosterSearchUrl',
        params: { search: search || '', page: page || 1 },
      }),

      // One cache entry per term so its pages accumulate; forceRefetch catches the page change.
      serializeQueryArgs: ({ queryArgs }) => queryArgs.search || '',

      forceRefetch: ({ currentArg, previousArg }) => currentArg?.page !== previousArg?.page,

      merge: (currentCache, newResponse, { arg }) => {
        if (arg.page === 1) {
          return newResponse
        }

        return {
          ...newResponse,
          members: [...(currentCache?.members || []), ...newResponse.members],
        }
      },

      providesTags: ['MasterRoster'],
    }),

    // Not MasterRoster: a refetch would merge the accumulated page twice; the panel hides the golfer.
    addMember: post(build, 'addMemberUrl', (memberId) => ({ member_id: memberId }), [ 'Roster' ]),

    createMember: post(build, 'createMemberManuallyUrl', (memberData) => memberData, [ 'Roster' ]),

    updateMember: build.mutation({
      query: ({ memberId, ...memberData }) => ({
        urlKey:  'updateMemberUrl',
        method:  'PATCH',
        params:  { member_id: memberId, ...memberData },
      }),
      invalidatesTags: ['Roster'],
    }),

    deleteMember: post(build, 'destroyMemberUrl', memberIdParams, [ 'Roster' ]),

    inviteMember: post(build, 'inviteMemberUrl', memberIdParams, [ 'Roster' ]),

    registerAll: post(build, 'registerAllUrl'),

    downloadRoster: post(build, 'downloadRosterUrl'),
  }),
})

export const {
  useGetRosterQuery,
  useSearchMasterRosterQuery,
  useAddMemberMutation,
  useCreateMemberMutation,
  useUpdateMemberMutation,
  useDeleteMemberMutation,
  useInviteMemberMutation,
  useRegisterAllMutation,
  useDownloadRosterMutation,
} = rosterApi
